import { BackgroundModel } from '../engine/models/background.model';
import { ColorUtils } from '../shared/libraries/colors/colors';
const depreactedUIConfigNames = [
    'ui-bd.class',
    'ui-play-pause',
    'ui-camera-mode',
    'ui-center',
    'ui-cross-section',
    'ui-dissect',
    'ui-fs',
    'ui-tools-display',
    'ui-tree',
    'ui-zoom',
    'ui-zoom-in',
    'ui-zoom-out',
    'ui-pan',
    'ui-share',
    'ui-keys',
    'ui-slider'
];
const depreactedNonUIConfigNames = [
    'o',
    'background.color-stops',
    'background.gradient-type',
    'dd',
    'dn',
    'image-display',
    'imageDisplay',
    'imageSrc',
    'pre',
    'referrer'
];
export const deprecatedConfigNames = [...depreactedUIConfigNames, ...depreactedNonUIConfigNames];
// normalizes legacy id params (s, m, be, b) into standard id
export function normalizeQueryIdParam(config) {
    const { id, s, m, be, b } = config;
    let contentId = '';
    if (id) { // pass-through id
        contentId = id;
    }
    else if (be) { // encoded bookmark id
        contentId = be;
    }
    else if (b) { // decoded bookmark id
        const bookmarkId = parseInt(b, 10);
        contentId = Number.isNaN(bookmarkId) ? '' : encodeBookmarkId(bookmarkId);
    }
    else if (m) { // module id
        const sex = s === 'female' ? 'female' : 'male';
        contentId = normalizeModuleId(m, sex);
    }
    // trim trailing .json 
    return contentId.replace(/\.json$/, '');
}
export function normalizeQueryTypeParam(config) {
    // determine type by id
    const contentId = normalizeQueryIdParam(config);
    return contentId.includes('/') ? 'module' : 'bookmark';
}
// ported from: https://github.com/biodigital-human/human-cms/blob/f8608ac6c523318b4a21ac76b200dbd3928abc1f/content/bookmarks.py#L46
function encodeBookmarkId(bookmarkId) {
    const BASE62_IDX = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const divmod = (x, y) => {
        const quotient = Math.floor(x / y);
        const remainder = x % y;
        return [quotient, remainder];
    };
    const encoded = [];
    let num = bookmarkId;
    let digit;
    while (num > 0) {
        [num, digit] = divmod(num, 62);
        encoded.unshift(BASE62_IDX[digit]);
    }
    return encoded.join('');
}
function normalizeModuleId(id, sex = 'male') {
    return id.includes('/')
        ? id // use full module name
        : `production/${sex}Adult/${id}`; // normalize short module names    
}
const ɵ0 = (params) => normalizeQueryTypeParam(params), ɵ1 = (p) => normalizeQueryIdParam(p), ɵ2 = (p) => p['bgstd'] || p['background.colors'] || p['background'];
// normalize into single params
const paramTransforms = {
    'type': ɵ0,
    'id': ɵ1,
    // background normalization
    'background': ɵ2
};
function dropDeprecatedParams(config) {
    const configKeys = Object.keys(config);
    const deprecatedKeys = configKeys
        .filter((key) => deprecatedConfigNames.includes(key));
    // warning: deprecated params
    if (deprecatedKeys.length > 0) {
        const messages = [
            `The following URL parameters have been deprecated: "${deprecatedKeys.join('", "')}"`,
            `Please contact support@biodigital.com for more information.`
        ];
        console.warn(messages.join('\n'));
    }
    return configKeys.reduce((acc, key) => {
        // drop if deprecated
        if (deprecatedConfigNames.includes(key)) {
            delete acc[key];
        }
        return acc;
    }, config);
}
function transformParams(config) {
    return Object.keys(paramTransforms)
        // exclude already defined params
        .filter(targetParamId => typeof config[targetParamId] === 'undefined')
        // transform many -> one params
        .reduce((acc, targetParamId) => {
        const transform = paramTransforms[targetParamId];
        const transformedValue = transform(config);
        if (typeof (transformedValue) !== 'undefined') {
            acc[targetParamId] = transformedValue;
        }
        return acc;
    }, config);
}
function toXYZ(values) {
    if (values.length === 3) {
        const { 0: x, 1: y, 2: z } = values;
        return { x, y, z };
    }
    else {
        return null;
    }
}
function parseCamera(input) {
    // comma seperated camera values -> number[]
    const values = input
        .split(',')
        .map((entry) => parseFloat(entry));
    // validate required 3 part camera values (eye required) + valid numbers
    const valid = values.length % 3 === 0 && !values.some((value) => Number.isNaN(value));
    if (valid) {
        // required: eye
        const eyeValues = values.slice(0, 3);
        // optional additions
        const lookValues = values.slice(3, 6);
        const upValues = values.slice(6, 9);
        return {
            eye: toXYZ(eyeValues),
            look: toXYZ(lookValues),
            up: toXYZ(upValues)
        };
    }
    else {
        return null;
    }
}
function parseBackground(input) {
    // parse color
    const colorValues = ColorUtils.getColors(input);
    if (colorValues) {
        // get colors (first + second) as 255
        const color1 = colorValues[0]['255'];
        const color2 = colorValues[1]['255'];
        // create background model
        const colors = [...color1, ...color2];
        const gradientType = 'radial';
        const colorStops = [0, 100];
        return new BackgroundModel(colors, gradientType, colorStops);
    }
    else {
        return null;
    }
}
const paramValueTransform = {
    'camera': parseCamera,
    'background': parseBackground
};
// coerce strings to booleans where appropriate
function coerceValues(config) {
    const coerceValue = (value) => {
        if (value) {
            const normalizedValue = value.toLowerCase();
            if (normalizedValue === 'true') {
                return true;
            }
            else if (normalizedValue === 'false') {
                return false;
            }
            else if (!isNaN(Number(normalizedValue))) {
                return Number(normalizedValue);
            }
        }
        return value;
    };
    return Object.keys(config).reduce((acc, key) => {
        // get custom value transformer else default
        const valueTransformer = paramValueTransform[key] || coerceValue;
        const value = valueTransformer(config[key]);
        // patch for JnJ
        // sanitize load rotate
        // Later: define coercion for each property instead of making assumptions.
        if (key === 'load-rotate' && isNaN(Number(value))) {
            return acc;
        }
        acc[key] = value;
        return acc;
    }, {});
}
export function getInitialWidgetConfig(rawParams) {
    const withoutDeprecated = dropDeprecatedParams(rawParams);
    const transformedConfig = transformParams(withoutDeprecated);
    const coercedConfig = coerceValues(transformedConfig);
    return coercedConfig;
}
export { ɵ0, ɵ1, ɵ2 };
